// src/pages/Banners/components/BannerFormModal.tsx - Fixed version
import React, {useState, useCallback, useEffect} from "react";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {X, Upload, Image as ImageIcon, AlertCircle} from "lucide-react";
import {useBanners} from "../../../hooks/useBanners";
import type {BannerResponseDto} from "../../../types/banner";

// Validation schema - Dynamic based on mode
const createBannerSchema = z.object({
  image: z.instanceof(File, {message: "Please select an image file"}),
  title: z.string().optional(),
  description: z.string().optional(),
});

const editBannerSchema = z.object({
  image: z.instanceof(File).optional(),
  title: z.string().optional(),
  description: z.string().optional(),
});

type BannerFormData = z.infer<typeof createBannerSchema>;

interface BannerFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: "create" | "edit";
  banner?: BannerResponseDto | null;
}

const BannerFormModal: React.FC<BannerFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  mode,
  banner,
}) => {
  const {
    handleCreate,
    handleUpdate,
    handleUpdateWithImage,
    isCreating,
    isUpdating,
    stats,
  } = useBanners();
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: {errors},
  } = useForm<BannerFormData>({
    resolver: zodResolver(
      mode === "create" ? createBannerSchema : editBannerSchema
    ),
    defaultValues: {
      title: "",
      description: "",
    },
  });

  const watchedImage = watch("image");
  const watchedTitle = watch("title");
  const watchedDescription = watch("description");

  // ✅ Set form values when banner data changes
  useEffect(() => {
    if (mode === "edit" && banner) {
      setValue("title", banner.title || "");
      setValue("description", banner.description || "");
      setPreviewUrl(banner.image);
    } else if (mode === "create") {
      setValue("title", "");
      setValue("description", "");
      setPreviewUrl(null);
    }
  }, [mode, banner, setValue]);

  // Handle file selection
  const handleFileSelect = useCallback(
    (file: File) => {
      if (file && file.type.startsWith("image/")) {
        setValue("image", file);
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      }
    },
    [setValue]
  );

  // Handle drag events
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const files = e.dataTransfer.files;
      if (files && files[0]) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // Handle form submission
  const onSubmit = async (data: BannerFormData) => {
    console.log("📝 Form submission data:", data);

    if (mode === "create") {
      const success = await handleCreate({
        file: data.image,
        title: data.title?.trim() || "",
        description: data.description?.trim() || "",
      });
      if (success) {
        reset();
        setPreviewUrl(null);
        onSuccess();
      }
    } else if (mode === "edit" && banner) {
      // Check if image was updated (new File object vs URL string)
      if (data.image && data.image instanceof File) {
        console.log("🖼️ Updating with new image");
        // Update with new image
        const updateData: {title?: string; description?: string} = {};
        if (data.title?.trim()) {
          updateData.title = data.title.trim();
        }
        if (data.description?.trim()) {
          updateData.description = data.description.trim();
        }
        const success = await handleUpdateWithImage(
          banner.id,
          data.image,
          updateData
        );
        if (success) {
          onSuccess();
        }
      } else {
        console.log("📝 Updating text fields only");
        // Update text fields only
        const updateData: {
          title?: string;
          description?: string;
        } = {};

        if (data.title?.trim()) {
          updateData.title = data.title.trim();
        }
        if (data.description?.trim()) {
          updateData.description = data.description.trim();
        }

        const success = await handleUpdate(banner.id, updateData);
        if (success) {
          onSuccess();
        }
      }
    }
  };

  // Handle modal close
  const handleClose = () => {
    reset();
    setPreviewUrl(mode === "edit" && banner ? banner.image : null);
    onClose();
  };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith("blob:")) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const isLoading = isCreating || isUpdating;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    {mode === "create" ? "Add New Banner" : "Edit Banner"}
                  </Dialog.Title>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Banner limit warning for create mode */}
                {mode === "create" && !stats.canCreate && (
                  <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Maximum 4 banners allowed. Please delete existing banners
                      to add new ones.
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* ✅ File Upload Area - Show for both create and edit modes */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Banner Image{" "}
                      {mode === "create" ? "*" : "(Click to change image)"}
                    </label>

                    <div
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                        dragActive
                          ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                          : "border-gray-300 dark:border-gray-600"
                      } ${
                        errors.image ? "border-red-300 dark:border-red-600" : ""
                      }`}
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      onClick={() =>
                        document.getElementById("file-upload")?.click()
                      }
                    >
                      {previewUrl ? (
                        <div className="space-y-4">
                          <img
                            src={previewUrl}
                            alt="Banner preview"
                            className="mx-auto max-h-40 rounded"
                          />
                          <div className="space-y-2">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {watchedImage instanceof File
                                ? "New image selected"
                                : "Current image"}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (mode === "edit") {
                                  // Reset to original image
                                  setPreviewUrl(banner?.image || null);
                                  setValue("image", undefined as any);
                                } else {
                                  // Remove image completely
                                  setPreviewUrl(null);
                                  setValue("image", undefined as any);
                                }
                              }}
                              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                              {mode === "edit"
                                ? "Cancel image change"
                                : "Remove image"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                          <div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              Drag and drop your image here, or{" "}
                              <span className="font-medium text-blue-600 hover:text-blue-500">
                                browse
                              </span>
                            </p>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, WebP up to 5MB
                            </p>
                          </div>
                        </div>
                      )}

                      <input
                        id="file-upload"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelect(file);
                        }}
                      />
                    </div>

                    {errors.image && (
                      <p className="text-sm text-red-600 dark:text-red-400 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.image.message}
                      </p>
                    )}
                  </div>

                  {/* Title Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Title (Optional)
                    </label>
                    <input
                      type="text"
                      {...register("title")}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter banner title for admin reference"
                    />
                  </div>

                  {/* Description Field */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description (Optional)
                    </label>
                    <textarea
                      {...register("description")}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Enter banner description"
                    />
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        isLoading ||
                        (mode === "create" &&
                          (!watchedImage || !stats.canCreate))
                      }
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoading
                        ? mode === "create"
                          ? "Creating..."
                          : "Updating..."
                        : mode === "create"
                        ? "Create Banner"
                        : "Update Banner"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BannerFormModal;
